// hooks/useTableSelection.ts
import { useState, useEffect, useRef, RefObject } from 'react';

interface TableSelectionReturn {
  selectedRows: string[];
  setSelectedRows: (value: string[] | ((prev: string[]) => string[])) => void;
  selectedDependentes: { [key: string]: string[] };
  setSelectedDependentes: (
    value:
      | { [key: string]: string[] }
      | ((prev: { [key: string]: string[] }) => { [key: string]: string[] }),
  ) => void;
  expandedRows: string[];
  setExpandedRows: (value: string[] | ((prev: string[]) => string[])) => void;
  headerCheckboxRef: RefObject<HTMLInputElement>;
  toggleSelect: (id: string) => void;
  toggleSelectDependente: (titularId: string, dependenteId: string) => void;
  toggleExpand: (id: string) => void;
  toggleAll: () => void;
  toggleAllDependentes: (titularId: string) => void;
}

export function useTableSelection(data: any[]): TableSelectionReturn {
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [selectedDependentes, setSelectedDependentes] = useState<{
    [key: string]: string[];
  }>({});
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);

  // Atualizar estado do checkbox principal
  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate =
        selectedRows.length > 0 && selectedRows.length < data.length;
    }
  }, [selectedRows, data.length]);

  const toggleSelect = (titularId: string) => {
    const willBeSelected = !selectedRows.includes(titularId);

    setSelectedRows(prev =>
      prev.includes(titularId)
        ? prev.filter(id => id !== titularId)
        : [...prev, titularId],
    );

    const titular = data.find(t => t.id === titularId);

    if (willBeSelected && titular) {
      const allDependentesIds = titular.dependentes?.map(dep => dep.id) || [];
      setSelectedDependentes(prev => ({
        ...prev,
        [titularId]: allDependentesIds,
      }));
    } else {
      setSelectedDependentes(prev => ({
        ...prev,
        [titularId]: [],
      }));
    }
  };

  const toggleSelectDependente = (titularId: string, dependenteId: string) => {
    setSelectedDependentes(prev => {
      const currentDeps = prev[titularId] || [];
      return {
        ...prev,
        [titularId]: currentDeps.includes(dependenteId)
          ? currentDeps.filter(id => id !== dependenteId)
          : [...currentDeps, dependenteId],
      };
    });
  };

  const toggleExpand = (titularId: string) => {
    setExpandedRows(prev =>
      prev.includes(titularId)
        ? prev.filter(id => id !== titularId)
        : [...prev, titularId],
    );
  };

  const toggleAll = () => {
    if (selectedRows.length === data.length) {
      setSelectedRows([]);
      setSelectedDependentes({});
    } else {
      const allTitularIds = data.map(titular => titular.id);
      setSelectedRows(allTitularIds);

      const allDependentes = data.reduce((acc, titular) => {
        acc[titular.id] = titular.dependentes?.map(dep => dep.id) || [];
        return acc;
      }, {} as { [key: string]: string[] });

      setSelectedDependentes(allDependentes);
    }
  };

  const toggleAllDependentes = (titularId: string) => {
    const titular = data.find(t => t.id === titularId);
    if (!titular) return;

    setSelectedDependentes(prev => {
      const allSelected =
        prev[titularId]?.length === titular.dependentes?.length;
      return {
        ...prev,
        [titularId]: allSelected
          ? []
          : titular.dependentes?.map(dep => dep.id) || [],
      };
    });
  };

  return {
    selectedRows,
    setSelectedRows,
    selectedDependentes,
    setSelectedDependentes,
    expandedRows,
    setExpandedRows,
    headerCheckboxRef,
    toggleSelect,
    toggleSelectDependente,
    toggleExpand,
    toggleAll,
    toggleAllDependentes,
  };
}
